import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { importAppUsers, type AdminRegistryApp } from '@/api/console';
import { Button, Modal } from '@/components/ui';

export interface ImportUsersModalProps {
    app: AdminRegistryApp | null;
    onClose: () => void;
}

const TEMPLATE_CSV = 'email,external_id,display_name\n';

function downloadTemplate() {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'danh_sach_nguoi_dung_mau.csv';
    link.click();
    URL.revokeObjectURL(url);
}

/**
 * EAA-1861 (design frame "EAA-1861-C Import users — tải lên theo khuôn mẫu"):
 * file-based counterpart to `SyncUsersModal`, for an app with no
 * `GET /directory/users` receiver of its own yet — same identity-minting
 * backend (`UserSyncService.import_records`), sourced from an uploaded CSV.
 *
 * The header row IS the schema (matches the open-record contract
 * `scim_client.list_users` already defines): only `email`/`external_id` are
 * load-bearing, everything else is read but never persisted server-side —
 * this modal does not attempt to validate or preview extra columns for the
 * same reason.
 */
export function ImportUsersModal({ app, onClose }: ImportUsersModalProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const [file, setFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const importMutation = useMutation({
        mutationFn: () =>
            importAppUsers(config.apiBase, accessToken as string, (app as AdminRegistryApp).code, file as File),
    });

    function handleClose() {
        if (importMutation.isPending) return;
        setFile(null);
        onClose();
    }

    function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
        setFile(event.target.files?.[0] ?? null);
    }

    return (
        <Modal
            open={Boolean(app)}
            onCancel={handleClose}
            title={t('console.apps.importUsersModal.title')}
            width={520}
            footer={
                <>
                    <Button onClick={handleClose} disabled={importMutation.isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => importMutation.mutate()}
                        disabled={!file || importMutation.isPending}
                    >
                        {importMutation.isPending
                            ? t('console.apps.importUsersModal.runningLabel')
                            : t('console.apps.importUsersModal.submitButton')}
                    </Button>
                </>
            }
        >
            <div className="apps-sync-users">
                <p>{t('console.apps.importUsersModal.description')}</p>

                <button type="button" className="apps-import-users__template-link" onClick={downloadTemplate}>
                    {t('console.apps.importUsersModal.downloadTemplateLink')}
                </button>

                <label className="apps-import-users__dropzone">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        onChange={handleFileChange}
                        hidden
                    />
                    {t('console.apps.importUsersModal.dropzoneLabel')}
                </label>

                {file && (
                    <div className="apps-import-users__file-chip">
                        <span className="apps-import-users__file-name">{file.name}</span>
                        <span className="apps-import-users__file-size">
                            {(file.size / 1024).toFixed(0)} KB
                        </span>
                    </div>
                )}

                {importMutation.isError && (
                    <p className="portal-admin-error">{t('console.apps.importUsersModal.errorMessage')}</p>
                )}

                {importMutation.data && (
                    <div className="apps-sync-users__stats">
                        <div className="apps-sync-users__stat apps-sync-users__stat--created">
                            <span className="apps-sync-users__stat-value">{importMutation.data.created}</span>
                            <span className="apps-sync-users__stat-label">
                                {t('console.apps.syncUsersModal.statCreated')}
                            </span>
                        </div>
                        <div className="apps-sync-users__stat apps-sync-users__stat--linked">
                            <span className="apps-sync-users__stat-value">{importMutation.data.linked}</span>
                            <span className="apps-sync-users__stat-label">
                                {t('console.apps.syncUsersModal.statLinked')}
                            </span>
                        </div>
                        <div className="apps-sync-users__stat apps-sync-users__stat--errors">
                            <span className="apps-sync-users__stat-value">
                                {importMutation.data.errors.length}
                            </span>
                            <span className="apps-sync-users__stat-label">
                                {t('console.apps.syncUsersModal.statErrors')}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}

export default ImportUsersModal;
